import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TEXT_FIELD_COMMIT_DELAY_MS } from "@/constants/form-input-constant";

export interface UseDraftValue {
  draft: string;
  setDraft: (next: string) => void;
  commit: () => void;
}

/** Buffers a controlled text value locally and hands it to `onCommit` after a pause, on `commit()`, and on unmount. */
export const useDraftValue = (
  value: string,
  onCommit: (value: string) => void,
  delayMs: number = TEXT_FIELD_COMMIT_DELAY_MS
): UseDraftValue => {
  const [draft, setDraftState] = useState(value);
  const draftRef = useRef(value);
  const committedRef = useRef(value);
  const onCommitRef = useRef(onCommit);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Assigned during render so an unmount flush sees the caller's newest closure.
  onCommitRef.current = onCommit;

  // An outside change (hydration, reset) replaces the draft; our own commits echo back and are ignored.
  useEffect(() => {
    if (value === committedRef.current) return;
    committedRef.current = value;
    draftRef.current = value;
    setDraftState(value);
  }, [value]);

  const commit = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const next = draftRef.current;
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommitRef.current(next);
  }, []);

  const setDraft = useCallback(
    (next: string) => {
      draftRef.current = next;
      setDraftState(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(commit, delayMs);
    },
    [commit, delayMs]
  );

  useEffect(
    () => () => {
      if (timerRef.current) commit();
    },
    [commit]
  );

  return useMemo(
    () => ({ draft, setDraft, commit }),
    [draft, setDraft, commit]
  );
};
